import { ReactElement, useEffect, useState } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useConfigs } from '@lib/hooks';
import { toast, Button, Card, Spinner } from '@heroui/react';
import { Plus } from '@icons/plus';
import GridTable from '@components/home/gridTable';
import { useAmplitude } from '@/amplitude/amplitudeContext';
import { AmplitudeEvent } from '@/amplitude/events';
import TemplateSelectionModal from '@components/templates/templateSelectionModal';
import { CountdownPosition, TimerType } from '@/types';
import { getTemplateById, TimerTemplateId } from '@lib/timerTemplates';

export default function HomePage(): ReactElement {
	const router = useRouter();
	const { amplitudeInstance } = useAmplitude();
	const { configs, isLoading, mutate, error } = useConfigs();
	const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);

	useEffect(() => {
		if (error) {
			toast.danger('Error', {
				description: error instanceof Error ? error.message : 'Something went wrong',
			});
		}
	}, [error]);

	const onClick = () => {
		setIsTemplateModalOpen(true);
	};

	const handleTemplateSelect = (
		templateId: TimerTemplateId,
		countdownPosition: CountdownPosition | undefined,
		timerType: TimerType,
	) => {
		const template = getTemplateById(templateId);
		const query: Record<string, string> = {
			template: templateId,
			timerType,
		};

		if (countdownPosition) {
			query.countdownPosition = countdownPosition;
		}

		amplitudeInstance?.track(AmplitudeEvent.TEMPLATE_SELECTED, { name: template?.analyticsName });
		amplitudeInstance?.track(AmplitudeEvent.TIMER_CREATED);
		setIsTemplateModalOpen(false);
		router.push({
			pathname: '/create',
			query,
		});
	};

	return (
		<div className='flex flex-col min-h-screen w-full max-w-[1239px] mx-auto px-4 mt-[25px]'>
			<div className='flex-grow'>
				<Card className='p-4 rounded-xl'>
					<Card.Content className='p-0'>
						<div className='flex items-center justify-between flex-col lg:flex-row gap-4'>
							<div className='flex items-center gap-3 flex-col lg:flex-row w-full lg:w-auto'>
								<div>
									<Image alt='config settings' src='/images/timer_settings.png' width={65} height={65} />
								</div>
								<div className='text-center lg:text-left'>
									<p className='font-lato font-bold text-base leading-6 text-customBlack'>Create new</p>
									<p className='font-lato font-normal text-base leading-6 text-customLabel'>
										Increase urgency and boost conversions by showing a countdown timer to your visitors.
									</p>
								</div>
							</div>
							<div className='w-full lg:w-auto'>
								<Button
									variant='primary'
									className='rounded-lg text-base leading-6 font-medium w-full lg:w-auto'
									onPress={onClick}
								>
									<Plus fill={'white'} />
									Create timer
								</Button>
							</div>
						</div>
					</Card.Content>
				</Card>
				<Card className='p-4 rounded-xl mt-5'>
					<Card.Header className='p-0'>
						<p className='font-lato font-bold text-base leading-6 text-customBlack'>Timers</p>
					</Card.Header>
					<Card.Content className='p-0 mt-6'>
						{isLoading && <Spinner />}
						{!isLoading && configs && (
							<GridTable data={configs} mutate={mutate} isEmpty={!isLoading && (!configs || configs.length === 0)} />
						)}
					</Card.Content>
				</Card>
			</div>
			<TemplateSelectionModal
				isOpen={isTemplateModalOpen}
				onOpenChange={setIsTemplateModalOpen}
				onSelect={handleTemplateSelect}
			/>
		</div>
	);
}
